/**
 * HTTP client with JWT auth.
 *
 * In Telegram WebApp: call /api/* on the same host (proxied by nginx).
 * In local dev: Vite proxy forwards /api → http://localhost:8000.
 */

import { getInitDataForRequest } from './initData';

const TOKEN_KEY = 'polybot.jwt';

// `localStorage` access throws (SecurityError), not just returns null, in
// an iOS WKWebView with storage blocked (private mode / "Block All
// Cookies"). These run on the auth path — an unguarded throw would blank
// the app. Degrade to an in-memory token so the session still works for
// this launch; it just won't survive a reload.
let memToken: string | null = null;

export function getToken(): string | null {
  try {
    // When storage is readable its value is authoritative (covers
    // cross-tab logout clearing the key); the in-memory copy is only a
    // fallback for when access itself throws.
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return memToken;
  }
}

export function setToken(token: string): void {
  memToken = token;
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage blocked — kept in memory for this session only */
  }
}

export function clearToken(): void {
  memToken = null;
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage blocked — nothing persisted to clear */
  }
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
// Endpoints whose body already carries init_data (auth/refresh/etc.)
// shouldn't have it duplicated in the header — the binding middleware
// exempts /v1/auth/* anyway, but skipping the header keeps requests
// tidy and avoids leaking the same string through two channels.
const NO_HEADER_PATH_PREFIXES = ['/v1/auth/'];
// Auth paths bypass the auto-refresh-on-401 dance — otherwise a failed
// refresh would loop trying to refresh itself.
const AUTH_PATH_PREFIX = '/v1/auth/';
// Mirror of ``init_data_binding.BINDING_ERROR_CODE``. A binding 401 is
// a cold-start race (``window.Telegram.WebApp.initData`` not populated
// yet when the first mutation fires), NOT a dead session — refreshing
// re-mints the same ``idh`` and 401s again. So we neither refresh nor
// clear the token / show the expired screen for it; the caller retries.
const INIT_DATA_BINDING_CODE = 'init_data_binding';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Payload = Record<string, unknown> | undefined;

// Fired once a non-auth request gets a 401 that a silent refresh could
// NOT recover — i.e. the session is genuinely dead (refresh cookie gone
// / expired / init_data binding can't be re-established). The app
// listens for this to show a "session expired, reload" surface instead
// of a generic "try again" toast that hides the real cause.
export const AUTH_EXPIRED_EVENT = 'polybot:auth-expired';

// Refresh proactively once 80% of the access-token lifetime has
// elapsed, leaving a 20% margin before expiry. A background tab whose
// timers were throttled wakes up on `visibilitychange` and re-arms.
const REFRESH_LEAD_FRACTION = 0.8;
const MIN_KEEPALIVE_MS = 30_000;
// After a failed proactive refresh (transient network blip), retry on
// this cadence rather than hammering — the on-demand 401 path is what
// ultimately declares the session dead.
const RETRY_KEEPALIVE_MS = 60_000;

let accessTokenExpiresAt = 0;
let keepaliveTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityBound = false;
// Authoritative on/off for the scheduler. The visibilitychange
// listener is bound once and never removed, so this is what stops it
// (and the retry loop) from re-arming after stop — e.g. once the
// session is declared dead and the expired overlay is up, a
// background→foreground bounce must NOT resume hitting /refresh.
let keepaliveActive = false;

// Single-flight refresh: 5 parallel 401s should trigger ONE /v1/auth/refresh,
// not 5. Whoever loses the race awaits the in-flight promise.
let inFlightRefresh: Promise<boolean> | null = null;
// Backoff before retrying a failed refresh once. ``/v1/auth/refresh``
// rotates the cookie on every call, so if a concurrent context (a
// second tab, or a reload firing mid-refresh) rotated it first, our
// cookie is momentarily stale → 401. Cookies live in one shared,
// HttpOnly jar that the browser auto-sends, so the winner's fresh
// secret is already there; one retry after this delay rides out the
// race instead of falsely logging the user out. A genuinely-dead
// cookie just fails again.
const REFRESH_RACE_RETRY_MS = 400;

async function refreshOnce(): Promise<boolean> {
  try {
    const res = await fetch(`/api/v1/auth/refresh`, {
      method: 'POST',
      // The refresh cookie is HttpOnly + SameSite=Strict; ``credentials:
      // 'include'`` is what tells the browser to actually attach it
      // (default is 'same-origin', which works in prod but not in dev
      // where webapp:5173 talks to api:8000).
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return false;
    const data = (await res.json()) as {
      access_token: string;
      expires_in?: number;
    };
    setToken(data.access_token);
    if (typeof data.expires_in === 'number') {
      accessTokenExpiresAt = Date.now() + data.expires_in * 1000;
      scheduleKeepalive();
    }
    return true;
  } catch {
    return false;
  }
}

async function tryRefresh(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    try {
      if (await refreshOnce()) return true;
      // Possible lost-rotation race — give the winner's Set-Cookie time
      // to land in the shared jar, then retry once with the now-fresh
      // secret the browser auto-sends.
      await new Promise((resolve) => setTimeout(resolve, REFRESH_RACE_RETRY_MS));
      return await refreshOnce();
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

function scheduleKeepalive(): void {
  if (keepaliveTimer) clearTimeout(keepaliveTimer);
  keepaliveTimer = null;
  if (!keepaliveActive) return;
  const remaining = accessTokenExpiresAt - Date.now();
  const delay =
    remaining > 0 ? Math.max(remaining * REFRESH_LEAD_FRACTION, MIN_KEEPALIVE_MS) : 0;
  keepaliveTimer = setTimeout(() => void proactiveRefresh(), delay);
}

async function proactiveRefresh(): Promise<void> {
  const ok = await tryRefresh();
  // On success ``tryRefresh`` re-armed the timer from the fresh
  // expiry. On failure, back off and retry — don't emit auth-expired
  // here (could be a transient blip; the next real request's 401 path
  // is the authority on a dead session).
  if (!ok && keepaliveActive) {
    if (keepaliveTimer) clearTimeout(keepaliveTimer);
    keepaliveTimer = setTimeout(() => void proactiveRefresh(), RETRY_KEEPALIVE_MS);
  }
}

/** Begin proactively refreshing the access token before it expires.
 * Call after a successful authenticate() with the server's
 * ``expires_in`` (seconds). Idempotent — re-arms the schedule. */
export function startSessionKeepalive(expiresInSeconds: number): void {
  accessTokenExpiresAt = Date.now() + expiresInSeconds * 1000;
  keepaliveActive = true;
  bindVisibility();
  scheduleKeepalive();
}

export function stopSessionKeepalive(): void {
  keepaliveActive = false;
  if (keepaliveTimer) {
    clearTimeout(keepaliveTimer);
    keepaliveTimer = null;
  }
}

function bindVisibility(): void {
  if (visibilityBound || typeof document === 'undefined') return;
  visibilityBound = true;
  document.addEventListener('visibilitychange', () => {
    if (!keepaliveActive || document.visibilityState !== 'visible') return;
    // Mobile WebViews freeze timers in the background, so the scheduled
    // refresh may be overdue when the user returns. Refresh now if we're
    // at/near expiry, otherwise just re-arm with the correct delay.
    if (accessTokenExpiresAt - Date.now() <= MIN_KEEPALIVE_MS) {
      void proactiveRefresh();
    } else {
      scheduleKeepalive();
    }
  });
}

// Peek the error ``code`` from a non-OK response without consuming its
// body (so the caller can still read it). Returns null on any parse
// failure — treated as "unknown", i.e. the normal refresh path.
async function peekErrorCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { error?: { code?: string } };
    return body?.error?.code ?? null;
  } catch {
    return null;
  }
}

function emitAuthExpired(): void {
  stopSessionKeepalive();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  }
}

async function doFetch(
  method: string,
  path: string,
  headers: Record<string, string>,
  body?: Payload,
): Promise<Response> {
  return fetch(`/api${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    // Send the refresh cookie on auth endpoints (same-origin in prod,
    // explicit-include in dev). Other endpoints don't need it — the
    // cookie's Path=/api/v1/auth keeps it scoped — but ``include`` is
    // safe across the board and simpler than per-path branching.
    credentials: 'include',
  });
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: Payload,
): Promise<T> {
  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Bind mutations to the WebApp initData (sec/jwt-bind-init-data-hash):
    // backend's InitDataBindingMiddleware refuses POST/PUT/PATCH/DELETE
    // whose sha256(X-Init-Data) doesn't match the JWT's idh claim. Reads
    // are gated by JWT TTL alone — we don't blow stack on every GET.
    if (
      MUTATION_METHODS.has(method) &&
      !NO_HEADER_PATH_PREFIXES.some((p) => path.startsWith(p))
    ) {
      const initData = getInitDataForRequest();
      if (initData) {
        headers['X-Init-Data'] = initData;
      }
    }
    return headers;
  };

  let res = await doFetch(method, path, buildHeaders(), body);

  // 401 on a non-auth endpoint → try one silent refresh + retry.
  // ``inFlightRefresh`` deduplicates parallel callers so 5 simultaneous
  // 401s don't fan out to 5 refresh requests. A binding-mismatch 401 is
  // skipped — it's a cold-start race, not a dead session (see
  // ``INIT_DATA_BINDING_CODE``).
  if (res.status === 401 && !path.startsWith(AUTH_PATH_PREFIX)) {
    const code = await peekErrorCode(res);
    if (code !== INIT_DATA_BINDING_CODE) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        res = await doFetch(method, path, buildHeaders(), body);
      }
    }
  }

  if (!res.ok) {
    let code = 'http_error';
    let message = res.statusText;
    let details: unknown = undefined;
    try {
      const errBody = await res.json();
      if (errBody?.error) {
        code = errBody.error.code ?? code;
        message = errBody.error.message ?? message;
        details = errBody.error.details;
      }
    } catch {
      // ignore parse error
    }
    // A 401 that survived the silent-refresh retry means the session is
    // dead — clear the token and signal the app to prompt a reload
    // instead of letting the caller show a generic failure toast. Two
    // exclusions: auth endpoints (handled by the sign-in flow), and a
    // binding-mismatch 401 (cold-start race — the token is still good
    // once initData populates, so a logout here would be a false one).
    if (
      res.status === 401 &&
      code !== INIT_DATA_BINDING_CODE &&
      !path.startsWith(AUTH_PATH_PREFIX)
    ) {
      clearToken();
      emitAuthExpired();
    }
    throw new ApiError(res.status, code, message, details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: Payload): Promise<T> =>
    request<T>('POST', path, body),
  put: <T>(path: string, body?: Payload): Promise<T> =>
    request<T>('PUT', path, body),
  patch: <T>(path: string, body?: Payload): Promise<T> =>
    request<T>('PATCH', path, body),
};
